import React, { useContext } from 'react';
import { DefaultEffects, FontIcon, Stack, Text } from '@fluentui/react';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';

export interface ISectionCardProps {
    icon: string;
    title: string;
    description: string;
    /** Optional element rendered on the right of the header (e.g. an action button). */
    headerAction?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * The badge used for every section icon on the Kusto pages. Shared so the cards and the cluster
 * banner cannot drift apart.
 */
export const SectionIcon: React.FC<{ icon: string }> = ({ icon }) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    return (
        <div
            style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                // Dark mode remaps palette.white to the surface gray, so a white glyph on a
                // themePrimary plate reads as near-black. Tint the plate instead.
                backgroundColor: theme.palette.themeLighter,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <FontIcon
                iconName={icon}
                style={{ fontSize: 20, color: theme.palette.themePrimary }}
            />
        </div>
    );
};

/**
 * A titled, elevated container that visually groups one section of a Kusto page:
 * an icon badge, a title, a short description, an optional header action, and the section body.
 */
export const SectionCard: React.FC<ISectionCardProps> = ({
    icon,
    title,
    description,
    headerAction,
    children,
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    return (
        <div
            style={{
                backgroundColor: theme.surfaces.container,
                border: `1px solid ${theme.surfaces.chromeBorder}`,
                borderRadius: 8,
                boxShadow: DefaultEffects.elevation4,
                padding: 24,
            }}
        >
            <Stack
                horizontal
                tokens={{ childrenGap: 14 }}
                verticalAlign="center"
            >
                <SectionIcon icon={icon} />
                <Stack tokens={{ childrenGap: 2 }} grow>
                    <Text
                        variant="large"
                        styles={{ root: { fontWeight: 600 } }}
                    >
                        {title}
                    </Text>
                    <Text
                        variant="small"
                        styles={{
                            root: { color: theme.palette.neutralSecondary },
                        }}
                    >
                        {description}
                    </Text>
                </Stack>
                {headerAction}
            </Stack>
            <div style={{ marginTop: 20 }}>{children}</div>
        </div>
    );
};
